import React, { useEffect, useState } from "react";
import { authFetch } from "../api";

const inputClass = "w-full px-4 py-2 rounded-lg bg-white/5 border border-navy-border text-white focus:outline-none focus:border-accent-light";

// A generic admin form for a single-row settings endpoint (GET to load, PATCH to save).
// fields: [{ key, label, type?: "text" | "textarea" | "checkbox", rows?, hint?, checkboxLabel? }] - or { heading } to add a divider.
export default function SettingsForm({ endpoint, title, intro, fields }) {
  const [data, setData] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    setData(null);
    authFetch(endpoint)
      .then((res) => res.json())
      .then(setData)
      .catch(() => setError("Failed to load."));
  }, [endpoint]);

  const save = async (e) => {
    e.preventDefault();
    setMessage("");
    setError("");
    const body = {};
    fields.forEach((f) => { if (f.key) body[f.key] = data[f.key]; });
    try {
      const res = await authFetch(endpoint, { method: "PATCH", body: JSON.stringify(body) });
      if (!res.ok) throw new Error();
      setData(await res.json());
      setMessage("Saved.");
    } catch {
      setError("Failed to save.");
    }
  };

  return (
    <div className="max-w-3xl">
      <h1 className="text-2xl font-bold mb-1">{title}</h1>
      {intro && <p className="text-sm text-gray-400 mb-6">{intro}</p>}
      {error && <p className="text-sm text-red-400 mb-4">{error}</p>}

      {!data ? (
        <p className="text-gray-400">Loading...</p>
      ) : (
        <form onSubmit={save} className="space-y-4">
          {fields.map((f, i) =>
            f.heading ? (
              <h2 key={i} className="text-lg font-bold text-white pt-4 border-t border-navy-border">{f.heading}</h2>
            ) : (
              <div key={f.key}>
                <label className="block text-sm text-gray-300 mb-1">{f.label}</label>
                {f.type === "checkbox" ? (
                  <label className="flex items-center gap-2 text-sm text-gray-200">
                    <input
                      type="checkbox"
                      checked={!!data[f.key]}
                      onChange={(e) => setData({ ...data, [f.key]: e.target.checked })}
                    />
                    {f.checkboxLabel || "Enabled"}
                  </label>
                ) : f.type === "textarea" ? (
                  <textarea
                    rows={f.rows || 3}
                    className={inputClass}
                    value={data[f.key] ?? ""}
                    onChange={(e) => setData({ ...data, [f.key]: e.target.value })}
                  />
                ) : (
                  <input
                    className={inputClass}
                    value={data[f.key] ?? ""}
                    onChange={(e) => setData({ ...data, [f.key]: e.target.value })}
                  />
                )}
                {f.hint && <p className="text-xs text-gray-500 mt-1">{f.hint}</p>}
              </div>
            )
          )}
          {message && <p className="text-sm text-green-400">{message}</p>}
          <button className="px-6 py-2 rounded-lg bg-brand-gradient text-white font-semibold">Save Changes</button>
        </form>
      )}
    </div>
  );
}
